'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherCheckCircle, FeatherCopy, FeatherExternalLink, FeatherInfo, FeatherRefreshCw, FeatherSearch, FeatherXCircle, FeatherX } from '@subframe/core';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Skeleton, TextField } from '@/shared/components/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearEventoDetalhe, fetchEventoDetalhe, selectEventoDetalhe, selectEventoDetalheLoading } from '../store';
import type { EventoConsumo, EventoItemDetalhado, EventoItemStatus, OrigemEventoTipo } from '../types';
import { formatarDataHora, formatarMoedaBR } from '../utils/apuracaoFormatters';
import { categoriaEventoLabel, eventoStatusConfig, modalidadeContratoLabel, modalidadePostalLabel, motivoRejeicaoLabel } from '../utils/apuracaoStatusConfig';
interface EventoDetalheDrawerProps {
  evento: EventoConsumo | null;
  isOpen: boolean;
  onClose: () => void;
  onReprocessar?: (eventoId: string) => void;
  podeReprocessar: boolean;
  saving: boolean;
}
function resolveOrigemUrl(tipo: OrigemEventoTipo, id: string): string | null {
  if (!id) return null;
  switch (tipo) {
    case 'NOTIFICACAO':
      return `/notificacoes/${id}`;
    case 'LOTE':
      return `/notificacoes/lotes/${id}`;
    case 'NEGOCIACAO':
      return `/negociacoes/${id}`;
    default:
      return null;
  }
}
export function EventoDetalheDrawer(t0) {
  const $ = _c(133);
  const {
    evento,
    isOpen,
    onClose,
    onReprocessar,
    podeReprocessar,
    saving
  } = t0;
  const router = useRouter();
  const dispatch = useAppDispatch();
  const enriched = useAppSelector(selectEventoDetalhe);
  const detalheLoading = useAppSelector(selectEventoDetalheLoading);
  const eventoId = evento?.id ?? null;
  let t1;
  let t2;
  if ($[0] !== dispatch || $[1] !== eventoId || $[2] !== isOpen) {
    t1 = () => {
      if (!isOpen || !eventoId) {
        return;
      }
      dispatch(fetchEventoDetalhe(eventoId));
      return () => {
        dispatch(clearEventoDetalhe());
      };
    };
    t2 = [isOpen, eventoId, dispatch];
    $[0] = dispatch;
    $[1] = eventoId;
    $[2] = isOpen;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (!isOpen || !evento) {
    return null;
  }
  const displayEvento = enriched?.id === evento.id ? enriched : evento;
  const statusConfig = eventoStatusConfig[displayEvento.status];
  const StatusIcon = statusConfig.icon;
  let t3;
  if ($[5] !== displayEvento.origemId || $[6] !== displayEvento.origemTipo) {
    t3 = resolveOrigemUrl(displayEvento.origemTipo, displayEvento.origemId);
    $[5] = displayEvento.origemId;
    $[6] = displayEvento.origemTipo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const origemUrl = t3;
  let t4;
  if ($[8] !== displayEvento.payloadBruto) {
    t4 = () => {
      const json = JSON.stringify(displayEvento.payloadBruto, null, 2);
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(json).catch(_temp);
      }
    };
    $[8] = displayEvento.payloadBruto;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleCopiarPayload = t4;
  let t5;
  if ($[10] !== origemUrl || $[11] !== router) {
    t5 = () => {
      if (origemUrl) {
        router.push(origemUrl);
      }
    };
    $[10] = origemUrl;
    $[11] = router;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleIrParaOrigem = t5;
  let t6;
  if ($[13] !== onClose) {
    t6 = <button type="button" aria-label="Fechar" onClick={onClose} className="absolute inset-0 cursor-default" />;
    $[13] = onClose;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = statusConfig.variant === "brand" ? "brand" : statusConfig.variant;
  let t8;
  if ($[15] !== StatusIcon) {
    t8 = <StatusIcon />;
    $[15] = StatusIcon;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== statusConfig.label || $[18] !== t7 || $[19] !== t8) {
    t9 = <Badge variant={t7} icon={t8}>{statusConfig.label}</Badge>;
    $[17] = statusConfig.label;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = categoriaEventoLabel[displayEvento.categoria];
  let t11;
  if ($[21] !== t10) {
    t11 = <span className="text-caption font-caption text-subtext-color">{t10}</span>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <div className="flex flex-wrap items-center gap-2">{t9}{t11}</div>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== displayEvento.origemLabel) {
    t13 = <h2 className="text-body-bold font-body-bold text-default-font truncate">{displayEvento.origemLabel}</h2>;
    $[26] = displayEvento.origemLabel;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== displayEvento.timestamp) {
    t14 = formatarDataHora(displayEvento.timestamp);
    $[28] = displayEvento.timestamp;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <span className="text-caption font-caption text-subtext-color">{t14}</span>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t12 || $[33] !== t13 || $[34] !== t15) {
    t16 = <div className="flex min-w-0 flex-1 flex-col gap-2">{t12}{t13}{t15}</div>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FeatherX className="h-4 w-4" />;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== onClose) {
    t18 = <button type="button" aria-label="Fechar drawer" onClick={onClose} className="bg-default-background flex h-8 w-8 items-center justify-center rounded-[8px] border border-solid border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-50">{t17}</button>;
    $[37] = onClose;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t16 || $[40] !== t18) {
    t19 = <header className="flex items-start justify-between gap-3 border-b border-solid border-neutral-200 p-5">{t16}{t18}</header>;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">Identificação</h3>;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <dt className="text-caption font-caption text-subtext-color">Tenant</dt>;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== displayEvento.tenantNome) {
    t22 = <div>{t21}<dd className="text-default-font font-medium">{displayEvento.tenantNome}</dd></div>;
    $[44] = displayEvento.tenantNome;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <dt className="text-caption font-caption text-subtext-color">Produto</dt>;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const t24 = displayEvento.produtoNome ?? "\u2014";
  let t25;
  if ($[47] !== t24) {
    t25 = <div>{t23}<dd className="text-default-font font-medium">{t24}</dd></div>;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <dt className="text-caption font-caption text-subtext-color">Origem</dt>;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== displayEvento.origemTipo) {
    t27 = <div>{t26}<dd className="text-default-font font-medium">{displayEvento.origemTipo}</dd></div>;
    $[50] = displayEvento.origemTipo;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <dt className="text-caption font-caption text-subtext-color">ID da origem</dt>;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== displayEvento.origemId) {
    t29 = <div>{t28}<dd className="text-default-font font-mono text-xs">{displayEvento.origemId}</dd></div>;
    $[53] = displayEvento.origemId;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== t22 || $[56] !== t25 || $[57] !== t27 || $[58] !== t29) {
    t30 = <section className="flex flex-col gap-3">{t20}<dl className="grid grid-cols-2 gap-3 rounded-[12px] bg-neutral-50 p-4 text-sm">{t22}{t25}{t27}{t29}</dl></section>;
    $[55] = t22;
    $[56] = t25;
    $[57] = t27;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">Cálculo aplicado</h3>;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <dt className="text-caption font-caption text-subtext-color">Quantidade</dt>;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== displayEvento.quantidade) {
    t33 = <div>{t32}<dd className="text-default-font font-medium">{displayEvento.quantidade}</dd></div>;
    $[62] = displayEvento.quantidade;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <dt className="text-caption font-caption text-subtext-color">Valor unitário</dt>;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== displayEvento.valorUnitario) {
    t35 = displayEvento.valorUnitario != null ? formatarMoedaBR(displayEvento.valorUnitario) : "\u2014";
    $[65] = displayEvento.valorUnitario;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t35) {
    t36 = <div>{t34}<dd className="text-default-font font-medium">{t35}</dd></div>;
    $[67] = t35;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== displayEvento.descontoAplicado) {
    t37 = displayEvento.descontoAplicado > 0 && <div><dt className="text-caption font-caption text-subtext-color">Desconto</dt><dd className="text-success-700 font-medium">− {formatarMoedaBR(displayEvento.descontoAplicado)}</dd></div>;
    $[69] = displayEvento.descontoAplicado;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <dt className="text-caption font-caption text-subtext-color">Valor cobrável</dt>;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== displayEvento.valorCobravel) {
    t39 = formatarMoedaBR(displayEvento.valorCobravel);
    $[72] = displayEvento.valorCobravel;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t39) {
    t40 = <div className="col-span-2 border-t border-neutral-200 pt-3">{t38}<dd className="text-default-font text-lg font-bold">{t39}</dd></div>;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== t33 || $[77] !== t36 || $[78] !== t37 || $[79] !== t40) {
    t41 = <section className="flex flex-col gap-3">{t31}<dl className="grid grid-cols-2 gap-3 rounded-[12px] bg-neutral-50 p-4 text-sm">{t33}{t36}{t37}{t40}</dl></section>;
    $[76] = t33;
    $[77] = t36;
    $[78] = t37;
    $[79] = t40;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] !== detalheLoading || $[82] !== displayEvento.id || $[83] !== enriched?.id) {
    t42 = detalheLoading && enriched?.id !== displayEvento.id && <><section className="flex flex-col gap-3"><h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">Snapshot do contrato no momento do evento</h3><Skeleton variant="rectangular" height={140} className="rounded-[12px]" /></section><section className="flex flex-col gap-3"><h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">Destinatários</h3><Skeleton variant="rectangular" height={200} className="rounded-[12px]" /></section></>;
    $[81] = detalheLoading;
    $[82] = displayEvento.id;
    $[83] = enriched?.id;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] !== displayEvento.snapshotContrato) {
    t43 = displayEvento.snapshotContrato && <section className="flex flex-col gap-3"><h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">Snapshot do contrato no momento do evento</h3><dl className="grid grid-cols-1 gap-2 rounded-[12px] bg-neutral-50 p-4 text-sm">{displayEvento.snapshotContrato.modalidade && <div className="flex justify-between"><dt className="text-subtext-color">Modalidade</dt><dd className="text-default-font font-medium">{modalidadeContratoLabel(displayEvento.snapshotContrato.modalidade)}</dd></div>}{displayEvento.snapshotContrato.valorCustomizado != null && <div className="flex justify-between"><dt className="text-subtext-color">Valor customizado</dt><dd className="text-default-font font-medium">{formatarMoedaBR(displayEvento.snapshotContrato.valorCustomizado)}</dd></div>}{displayEvento.snapshotContrato.percentualDesconto != null && <div className="flex justify-between"><dt className="text-subtext-color">Desconto</dt><dd className="text-default-font font-medium">{displayEvento.snapshotContrato.percentualDesconto}%</dd></div>}{displayEvento.snapshotContrato.modalidadePostal && <div className="flex justify-between"><dt className="text-subtext-color">Modalidade postal</dt><dd className="text-default-font font-medium">{modalidadePostalLabel(displayEvento.snapshotContrato.modalidadePostal)}</dd></div>}{displayEvento.snapshotContrato.boletoCorreio && <div className="flex justify-between"><dt className="text-subtext-color">Boleto correio</dt><dd className="text-default-font font-medium">{displayEvento.snapshotContrato.boletoCorreio}</dd></div>}</dl></section>;
    $[85] = displayEvento.snapshotContrato;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== displayEvento.motivoRejeicao || $[88] !== displayEvento.sugestaoCorrecao) {
    t44 = displayEvento.motivoRejeicao && <section className="border-error-200 bg-error-50 flex flex-col gap-2 rounded-[12px] border p-4"><h3 className="text-caption-bold font-caption-bold text-error-700 uppercase">Motivo da rejeição</h3><p className="text-error-800 font-semibold">{motivoRejeicaoLabel[displayEvento.motivoRejeicao]}</p>{displayEvento.sugestaoCorrecao && <p className="text-error-700 text-sm">💡 {displayEvento.sugestaoCorrecao}</p>}</section>;
    $[87] = displayEvento.motivoRejeicao;
    $[88] = displayEvento.sugestaoCorrecao;
    $[89] = t44;
  } else {
    t44 = $[89];
  }
  let t45;
  if ($[90] !== displayEvento.itensDetalhados || $[91] !== displayEvento.itensTituloRotulo) {
    t45 = displayEvento.itensDetalhados && displayEvento.itensDetalhados.length > 0 && <EventoItensSection titulo={displayEvento.itensTituloRotulo ?? "Itens"} itens={displayEvento.itensDetalhados} />;
    $[90] = displayEvento.itensDetalhados;
    $[91] = displayEvento.itensTituloRotulo;
    $[92] = t45;
  } else {
    t45 = $[92];
  }
  let t46;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">Payload bruto</h3>;
    $[93] = t46;
  } else {
    t46 = $[93];
  }
  let t47;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <FeatherCopy />;
    $[94] = t47;
  } else {
    t47 = $[94];
  }
  let t48;
  if ($[95] !== handleCopiarPayload) {
    t48 = <div className="flex items-center justify-between">{t46}<Button variant="neutral-tertiary" size="small" icon={t47} onClick={handleCopiarPayload}>Copiar JSON</Button></div>;
    $[95] = handleCopiarPayload;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== displayEvento.payloadBruto) {
    t49 = JSON.stringify(displayEvento.payloadBruto, null, 2);
    $[97] = displayEvento.payloadBruto;
    $[98] = t49;
  } else {
    t49 = $[98];
  }
  let t50;
  if ($[99] !== t49) {
    t50 = <pre className="max-h-72 overflow-auto rounded-[12px] bg-neutral-900 p-4 text-xs text-neutral-100">{t49}</pre>;
    $[99] = t49;
    $[100] = t50;
  } else {
    t50 = $[100];
  }
  let t51;
  if ($[101] !== t48 || $[102] !== t50) {
    t51 = <section className="flex flex-col gap-3">{t48}{t50}</section>;
    $[101] = t48;
    $[102] = t50;
    $[103] = t51;
  } else {
    t51 = $[103];
  }
  let t52;
  if ($[104] !== t30 || $[105] !== t41 || $[106] !== t42 || $[107] !== t43 || $[108] !== t44 || $[109] !== t45 || $[110] !== t51) {
    t52 = <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-5">{t30}{t41}{t42}{t43}{t44}{t45}{t51}</div>;
    $[104] = t30;
    $[105] = t41;
    $[106] = t42;
    $[107] = t43;
    $[108] = t44;
    $[109] = t45;
    $[110] = t51;
    $[111] = t52;
  } else {
    t52 = $[111];
  }
  let t53;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <FeatherExternalLink />;
    $[112] = t53;
  } else {
    t53 = $[112];
  }
  const t54 = !origemUrl;
  const t55 = origemUrl ? "Abrir registro de origem" : "Origem n\xE3o naveg\xE1vel";
  let t56;
  if ($[113] !== handleIrParaOrigem || $[114] !== t54 || $[115] !== t55) {
    t56 = <Button variant="neutral-secondary" size="medium" icon={t53} disabled={t54} onClick={handleIrParaOrigem} title={t55}>Ir para origem</Button>;
    $[113] = handleIrParaOrigem;
    $[114] = t54;
    $[115] = t55;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  let t57;
  if ($[117] !== displayEvento.id || $[118] !== displayEvento.status || $[119] !== onReprocessar || $[120] !== podeReprocessar || $[121] !== saving) {
    t57 = displayEvento.status === "REJEITADO" && onReprocessar && <Button variant="brand-primary" size="medium" icon={<FeatherRefreshCw />} onClick={() => onReprocessar(displayEvento.id)} loading={saving} disabled={saving || !podeReprocessar}>Reprocessar evento</Button>;
    $[117] = displayEvento.id;
    $[118] = displayEvento.status;
    $[119] = onReprocessar;
    $[120] = podeReprocessar;
    $[121] = saving;
    $[122] = t57;
  } else {
    t57 = $[122];
  }
  let t58;
  if ($[123] !== t56 || $[124] !== t57) {
    t58 = <footer className="flex items-center justify-end gap-3 border-t border-solid border-neutral-200 bg-white p-5">{t56}{t57}</footer>;
    $[123] = t56;
    $[124] = t57;
    $[125] = t58;
  } else {
    t58 = $[125];
  }
  let t59;
  if ($[126] !== t19 || $[127] !== t52 || $[128] !== t58) {
    t59 = <aside className="bg-default-background relative flex h-full w-full max-w-xl flex-col overflow-hidden">{t19}{t52}{t58}</aside>;
    $[126] = t19;
    $[127] = t52;
    $[128] = t58;
    $[129] = t59;
  } else {
    t59 = $[129];
  }
  let t60;
  if ($[130] !== t59 || $[131] !== t6) {
    t60 = <div className="fixed inset-0 z-[9999] flex justify-end bg-black/60">{t6}{t59}</div>;
    $[130] = t59;
    $[131] = t6;
    $[132] = t60;
  } else {
    t60 = $[132];
  }
  return t60;
}
function _temp() {
  return null;
}
interface EventoItensSectionProps {
  titulo: string;
  itens: EventoItemDetalhado[];
}
const ITEMS_VISIVEIS_INICIAL = 20;
function EventoItensSection(t0) {
  const $ = _c(22);
  const {
    titulo,
    itens
  } = t0;
  const [busca, setBusca] = useState("");
  const [mostrarTodos, setMostrarTodos] = useState(false);
  let t1;
  if ($[0] !== busca || $[1] !== itens) {
    bb0: {
      const buscaNormalizada = busca.trim().toLowerCase();
      if (!buscaNormalizada) {
        t1 = itens;
        break bb0;
      }
      t1 = itens.filter(item => {
        if (item.label.toLowerCase().includes(buscaNormalizada)) {
          return true;
        }
        if (item.descricao && item.descricao.toLowerCase().includes(buscaNormalizada)) {
          return true;
        }
        return false;
      });
    }
    $[0] = busca;
    $[1] = itens;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const filtrados = t1;
  let t2;
  if ($[3] !== filtrados || $[4] !== mostrarTodos) {
    t2 = mostrarTodos || filtrados.length <= ITEMS_VISIVEIS_INICIAL ? filtrados : filtrados.slice(0, ITEMS_VISIVEIS_INICIAL);
    $[3] = filtrados;
    $[4] = mostrarTodos;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const visiveis = t2;
  const ocultos = filtrados.length - visiveis.length;
  const showBusca = itens.length > 10;
  let t3;
  if ($[6] !== itens.length) {
    t3 = <span className="text-default-font ml-2 text-sm font-medium normal-case">({itens.length})</span>;
    $[6] = itens.length;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3 || $[9] !== titulo) {
    t4 = <div className="flex items-center justify-between"><h3 className="text-caption-bold font-caption-bold text-subtext-color uppercase">{titulo}{t3}</h3></div>;
    $[8] = t3;
    $[9] = titulo;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== busca || $[12] !== showBusca) {
    t5 = showBusca && <div className="relative"><FeatherSearch className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" /><TextField><TextField.Input placeholder={"Buscar por nome ou descri\xE7\xE3o..."} value={busca} onChange={e => setBusca(e.target.value)} className="pl-9" /></TextField></div>;
    $[11] = busca;
    $[12] = showBusca;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== filtrados.length || $[15] !== ocultos || $[16] !== visiveis) {
    t6 = filtrados.length === 0 ? <div className="rounded-[12px] bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">Nenhum item corresponde à busca.</div> : <><ul className="bg-default-background flex max-h-96 flex-col divide-y divide-neutral-100 overflow-y-auto rounded-[12px] border border-solid border-neutral-200">{visiveis.map(_temp3)}</ul>{ocultos > 0 && <button type="button" onClick={() => setMostrarTodos(true)} className="text-brand-600 hover:text-brand-700 text-caption-bold font-caption-bold self-center py-2">Mostrar mais {ocultos} {ocultos === 1 ? "item" : "itens"}</button>}</>;
    $[14] = filtrados.length;
    $[15] = ocultos;
    $[16] = visiveis;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <section className="flex flex-col gap-3">{t4}{t5}{t6}</section>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function _temp3(item_0, index) {
  return <li key={item_0.id} className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50"><span className="text-caption font-caption text-subtext-color mt-1 w-6 shrink-0 text-right">{index + 1}</span><div className="flex min-w-0 flex-1 flex-col gap-0.5"><span className="text-body font-body text-default-font truncate">{item_0.label}</span>{item_0.descricao && <span className="text-caption font-caption text-subtext-color truncate">{item_0.descricao}</span>}{item_0.metadata && Object.entries(item_0.metadata).map(_temp2)}</div>{item_0.valor != null && <span className="text-caption-bold font-caption-bold text-default-font shrink-0">{formatarMoedaBR(item_0.valor)}</span>}{item_0.statusLabel && <EventoItemStatusBadge item={item_0} />}</li>;
}
function _temp2(t0) {
  const [k, v] = t0;
  return <span key={k} className="text-caption font-caption text-subtext-color">{k}: <span className="text-default-font">{v}</span></span>;
}
const STATUS_ICON_BY_VARIANT: Record<EventoItemStatus, React.ReactNode> = {
  SUCESSO: <FeatherCheckCircle className="h-3 w-3" />,
  PENDENTE: <FeatherInfo className="h-3 w-3" />,
  FALHA: <FeatherXCircle className="h-3 w-3" />,
  INFO: <FeatherInfo className="h-3 w-3" />
};
const STATUS_BADGE_VARIANT: Record<EventoItemStatus, 'success' | 'warning' | 'error' | 'neutral'> = {
  SUCESSO: 'success',
  PENDENTE: 'warning',
  FALHA: 'error',
  INFO: 'neutral'
};
function EventoItemStatusBadge(t0) {
  const $ = _c(4);
  const {
    item
  } = t0;
  if (!item.status || !item.statusLabel) {
    return null;
  }
  const t1 = STATUS_BADGE_VARIANT[item.status];
  const t2 = STATUS_ICON_BY_VARIANT[item.status];
  let t3;
  if ($[0] !== item.statusLabel || $[1] !== t1 || $[2] !== t2) {
    t3 = <Badge variant={t1} icon={t2}>{item.statusLabel}</Badge>;
    $[0] = item.statusLabel;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}